import { useEffect, useLayoutEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { zIndex } from "@repo/tokens";
import { useAuthStore, useIsAuthenticated } from "../../store/useAuthStore";
import { PageLoader } from "../common/PageLoader";
import { DeepLinkHandler } from "./DeepLinkHandler";
import { parseLink } from "./parseLink";
import {
  isPendingDashboardDeepLinkRoute,
  needsDeepLinkProfileSync,
} from "./profileSync";

const SPINNER_DELAY_MS = 500;

/** Above modal overlays ({@link zIndex.modal}) so sync blocks interaction on open dialogs. */
const SYNC_BLOCKER_Z = zIndex.modal + 1;

export const DeepLinkSyncGuard = () => {
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();
  const profilesData = useAuthStore((s) => s.profilesData);
  const isSyncing = useAuthStore((s) => s.isSyncing);
  const setIsSyncing = useAuthStore((s) => s.setIsSyncing);

  const pendingDashboardDeepLink = isPendingDashboardDeepLinkRoute(
    location.pathname,
    location.search,
  );

  const blockChildRoutes = isSyncing || pendingDashboardDeepLink;
  const showSpinner = useDelayedFlag(blockChildRoutes, SPINNER_DELAY_MS);

  /**
   * Block child routes before paint when a deeplink requires grade/exam sync so
   * dashboard APIs do not run with the pre-sync token or stale profile.
   */
  useLayoutEffect(() => {
    if (!isAuthenticated) return;

    const parsed = parseLink(
      `${window.location.origin}${location.pathname}${location.search}`,
    );
    if (!parsed || !needsDeepLinkProfileSync(parsed, profilesData)) return;

    setIsSyncing(true);
  }, [
    isAuthenticated,
    location.pathname,
    location.search,
    profilesData,
    setIsSyncing,
  ]);

  return (
    <>
      <DeepLinkHandler />
      {!blockChildRoutes ? <Outlet /> : null}
      {blockChildRoutes ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: SYNC_BLOCKER_Z,
            pointerEvents: "auto",
            display: "flex",
            flexDirection: "column",
            backgroundColor: showSpinner ? undefined : "transparent",
          }}
        >
          {showSpinner ? <PageLoader /> : null}
        </div>
      ) : null}
    </>
  );
};

function useDelayedFlag(flag: boolean, delayMs: number): boolean {
  const [delayed, setDelayed] = useState(false);

  useEffect(() => {
    if (!flag) {
      setDelayed(false);
      return;
    }
    const id = window.setTimeout(() => setDelayed(true), delayMs);
    return () => window.clearTimeout(id);
  }, [flag, delayMs]);

  return delayed;
}
